import { Box, Tooltip } from '@mui/material';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components';
import { PaloSegmentedToggle } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { WarningAmberOutlined } from '@mui/icons-material';
import { deliveryDateTypeOrder, getDeliveryDateTypeToggleTranslationId } from '@synergy/models';
import { tokens } from '@synergy/theme-palo';
import dayjs from 'dayjs';

import { formatCalendarWeek } from '../../utils/calendar-week';

export interface PaloDeliveryDateControlProps {
    /** Formik path of the kind: by latest, fixed or wished. */
    typeName: string;
    /** Formik path of the date. */
    dateName: string;
    readonly?: boolean;
    disabled?: boolean;
    /**
     * Shows the date's validation message under the line — for a control that
     * stands on its own, e.g. in the head of a customer card, without a field
     * around it to carry the message.
     */
    withError?: boolean;
}

/**
 * When to deliver: the kind of date, the date and its calendar week in one line.
 * Suppliers confirm in weeks, so the week belongs next to the date (#2185);
 * the line keeps a head row from growing a row of its own (#2251).
 */
export const PaloDeliveryDateControl = ({
    typeName,
    dateName,
    readonly,
    disabled,
    withError,
}: PaloDeliveryDateControlProps): ReactElement => {
    const intl = useIntl();
    const { values, errors, touched } = useFormikContext();
    const date = getIn(values, dateName);
    const week = formatCalendarWeek(date);
    // A date gone by is a slip while the document is still being written; on
    // one that is through it is history and says nothing (#2251).
    const inThePast = !readonly && !disabled && !!date && dayjs(date).isBefore(dayjs(), 'day');
    const error = withError && getIn(touched, dateName) ? (getIn(errors, dateName) as string | undefined) : undefined;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <Field name={typeName}>
                {({ field, form }: FieldProps<string>) => (
                    <PaloSegmentedToggle
                        value={field.value}
                        options={deliveryDateTypeOrder.map(type => ({
                            value: type,
                            label: <FormattedMessage id={getDeliveryDateTypeToggleTranslationId(type)} />,
                        }))}
                        onChange={value => form.setFieldValue(field.name, value)}
                        disabled={readonly || disabled}
                    />
                )}
            </Field>
            {/* Date, week and a warning keep together on the line; they wrap
                under the options as one rather than leave the week alone
                (#2251). */}
            <Box sx={{ flex: '1 1 190px', minWidth: 190, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormikPoolDatePicker
                        name={dateName}
                        readOnly={readonly}
                        disabled={disabled}
                        disablePast={true}
                        quietPast={true}
                    />
                </Box>
                {week && <Box sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'nowrap' }}>{week}</Box>}
                {inThePast && (
                    <Tooltip title={intl.formatMessage({ id: 'app.common.deliveryDate.inThePast' })} placement="top">
                        <WarningAmberOutlined
                            aria-label={intl.formatMessage({ id: 'app.common.deliveryDate.inThePast' })}
                            sx={{ fontSize: 16, color: tokens.color.warn, flexShrink: 0 }}
                        />
                    </Tooltip>
                )}
            </Box>
            {error && (
                <Box sx={{ flexBasis: '100%', fontSize: '10.5px', color: 'error.main' }}>
                    <FormattedMessage id={error} />
                </Box>
            )}
        </Box>
    );
};
